import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import useSeo from "@/hooks/useSeo";

const TIMELINE = [
  { year: "2018", title: "正式出道", desc: "出演首部短片作品，开始受到业内关注。" },
  { year: "2020", title: "《白昼流星》", desc: "以细腻的青春表演被更多观众认识。" },
  { year: "2021", title: "《深海回响》", desc: "首次挑战科幻题材，拓宽表演边界。" },
  { year: "2022", title: "《雾中来信》《归途列车》", desc: "在文艺与都市题材中游刃有余。" },
  { year: "2023", title: "《无声告白》《春山可望》", desc: "表演生涯的重要转折，获得广泛认可。" },
  { year: "2024", title: "《长夜将尽》《逆风局》", desc: "挑战犯罪悬疑与职场题材，口碑持续走高。" },
  { year: "2025", title: "《光年之外》", desc: "与长期合作团队再度携手，探索更宏大的叙事。" },
];

const VALUES = [
  { icon: "ri-heart-line", title: "真诚", desc: "从不敷衍角色，用最真实的情绪贴近每一个人物。" },
  { icon: "ri-focus-3-line", title: "专注", desc: "把注意力放在作品本身，让表演说话。" },
  { icon: "ri-plant-line", title: "生长", desc: "不断尝试新题材，在变化中保持创作的活力。" },
];

export default function About() {
  useSeo({
    title: "关于筱田优 - 品牌故事与影视历程 | 筱田优官网",
    description:
      "了解筱田优的品牌故事与影视历程，从出道到代表作品，全面认识这位专注表演的影视创作者。",
    keywords: "关于筱田优,品牌故事,影视历程,演员介绍",
  });

  return (
    <div className="w-full pt-20 md:pt-24">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb items={[{ label: "关于" }]} />
          <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 mt-4">
            关于筱田优
          </h1>
          <p className="text-sm md:text-lg text-foreground-600 mt-4 max-w-3xl leading-relaxed">
            一个专注于表演的影视品牌，也是一段仍在继续的光影旅程。
          </p>
        </div>
      </div>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="rounded-lg overflow-hidden bg-background-200 aspect-[4/3]">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20empty%20film%20set%20with%20soft%20directional%20lighting%2C%20vintage%20camera%20and%20wooden%20floor%2C%20warm%20amber%20highlights%20against%20deep%20indigo%20shadows%2C%20quiet%20and%20reflective%20mood%2C%20no%20text%2C%20high%20detail&width=1200&height=900&seq=about-c01&orientation=landscape"
            alt="筱田优影视品牌拍摄现场"
            title="筱田优影视品牌 - 拍摄现场"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 leading-tight">
            品牌故事
          </h2>
          <div className="mt-5 flex flex-col gap-4 text-base text-foreground-600 leading-loose">
            <p>
              「筱田优」始于一份对表演的执着。从最初的短片创作到如今的电影与电视剧作品，
              这个品牌始终以作品为核心，用一部又一部真诚的作品与观众对话。
            </p>
            <p>
              我们相信，好的表演来自对生活的观察与理解。无论是犯罪悬疑中的克制，还是年代剧里的温柔，
              每一次诠释都是一次重新认识世界的过程。
            </p>
            <p>
              这里不仅记录作品，也记录创作背后的思考——关于角色，关于时间，关于人与人之间细微的连接。
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="w-full px-4 md:px-8">
          <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 flex items-center gap-3">
            <span className="w-1.5 h-7 rounded-full bg-accent-500" />
            影视历程
          </h2>
          <div className="mt-9 relative">
            <div className="absolute left-[7px] md:left-[9px] top-2 bottom-2 w-0.5 bg-background-300" />
            <div className="flex flex-col gap-8">
              {TIMELINE.map((item) => (
                <div key={item.year} className="relative pl-8 md:pl-12">
                  <span className="absolute left-0 top-1.5 w-4 h-4 md:w-5 md:h-5 rounded-full bg-accent-500 border-4 border-background-100" />
                  <span className="font-heading text-lg font-black text-primary-600">{item.year}</span>
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mt-1">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base text-foreground-500 mt-1.5 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 text-center">
          我们的坚持
        </h2>
        <p className="text-sm md:text-base text-foreground-500 text-center mt-3 max-w-xl mx-auto">
          支撑每一部作品的，是这些没有写在字幕里的态度。
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {VALUES.map((v) => (
            <div key={v.title} className="p-6 rounded-lg bg-background-50 border border-background-200 text-center">
              <div className="w-14 h-14 mx-auto rounded-md bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                <i className={`${v.icon} text-2xl`} />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
              <p className="text-sm text-foreground-500 mt-2 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-16">
        <div className="rounded-lg bg-primary-900 text-primary-50 px-6 md:px-12 py-10 md:py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">团队与声明</h2>
            <p className="text-sm text-primary-100/80 mt-3 max-w-2xl leading-relaxed">
              「筱田优」影视品牌官方网站由内容团队独立运营，站内作品资料与资讯均以演示为目的整理呈现。
              我们欢迎影迷通过留言与我们交流，也欢迎更多创作者一同加入光影的旅程。
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 px-6 h-11 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap flex items-center gap-2"
          >
            联系我们
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </div>
  );
}